import React, {useState, useEffect} from "react";
import {Modal, Button, Form} from "react-bootstrap";
import {useTranslation} from "react-i18next";

export const EditUser = ({ show, user, onClose, onSave }: any) => {
    const {t} = useTranslation();
    const [formState, setFormState] = useState({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        role: '',
        mobile: '',
        active: true
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (user) {
            setFormState({
                username: user.username || user.email || '',
                email: user.email || '',
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                role: user.role || user.roles?.[0] || '',
                mobile: user.mobile || '',
                active: user.active !== undefined ? user.active : true
            });
            setError(null);
        } else {
            setFormState({
                username: '',
                email: '',
                firstName: '',
                lastName: '',
                role: '',
                mobile: '',
                active: true
            });
        }
    }, [user]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        setFormState({ 
            ...formState, 
            [name]: type === 'checkbox' ? checked : value 
        });
    };

    const handleSubmit = async () => {
        // Validation
        setError(null);
        
        if (!formState.email || !formState.email.trim()) {
            setError(t('UserList.validation_email_required') || 'Email is required');
            return;
        }
        
        if (!formState.firstName || !formState.firstName.trim()) {
            setError(t('UserList.validation_firstname_required') || 'First name is required');
            return;
        }
        
        if (!formState.lastName || !formState.lastName.trim()) {
            setError(t('UserList.validation_lastname_required') || 'Last name is required');
            return;
        }
        
        if (!formState.role) {
            setError(t('UserList.validation_role_required') || 'Role is required');
            return;
        }

        setIsSubmitting(true);

        try {
            const updatedUser = {
                ...formState,
                id: user?.id,
                userId: user?.userId || user?.id
            };
            await onSave(updatedUser);
        } catch (error: any) {
            setError(error?.message || t('UserList.validation_failed') || 'Validation failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    const roleOptions = [
        { value: 'ROLE_COMPANY_ADMIN', label: t('UserList.role_company_admin') || 'Company Admin' },
        { value: 'ROLE_STORE_MANAGER', label: t('UserList.role_store_manager') || 'Store Manager' },
        { value: 'ROLE_AREA_MANAGER', label: t('UserList.role_area_manager') || 'Area Manager' }
    ];

    return (
        <Modal show={show} onHide={onClose} backdrop="static" centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{t('UserList.edit_user') || 'Edit User'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('UserList.username') || 'Username'}</Form.Label>
                        <Form.Control
                            type="text"
                            name="username"
                            value={formState.username}
                            onChange={handleChange}
                            placeholder={t('UserList.username_placeholder') || 'Enter username'}
                        />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>{t('UserList.email') || 'Email'} *</Form.Label>
                        <Form.Control
                            type="email"
                            name="email"
                            value={formState.email}
                            onChange={handleChange}
                            placeholder={t('UserList.email_placeholder') || 'Enter email'}
                        />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>{t('UserList.first_name') || 'First Name'} *</Form.Label>
                        <Form.Control
                            type="text"
                            name="firstName"
                            value={formState.firstName}
                            onChange={handleChange}
                            placeholder={t('UserList.first_name_placeholder') || 'Enter first name'}
                        />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>{t('UserList.last_name') || 'Last Name'} *</Form.Label>
                        <Form.Control
                            type="text"
                            name="lastName"
                            value={formState.lastName}
                            onChange={handleChange}
                            placeholder={t('UserList.last_name_placeholder') || 'Enter last name'}
                        />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>{t('UserList.role') || 'Role'} *</Form.Label>
                        <Form.Select
                            name="role"
                            value={formState.role}
                            onChange={handleChange}
                        >
                            <option value="">{t('UserList.select_role') || 'Select role'}</option>
                            {roleOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>{t('UserList.mobile') || 'Mobile'}</Form.Label>
                        <Form.Control
                            type="text"
                            name="mobile"
                            value={formState.mobile}
                            onChange={handleChange}
                            placeholder={t('UserList.mobile_placeholder') || 'Enter mobile number'}
                        />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Check
                            type="switch"
                            name="active"
                            label={t('UserList.active') || 'Active'}
                            checked={formState.active}
                            onChange={handleChange}
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="dark" onClick={onClose} disabled={isSubmitting}>
                    {t('RuleList.cancel') || 'Cancel'}
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? (t('RuleList.saving') || 'Saving...') : (t('RuleList.save') || 'Save')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

